import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, MapPin, Zap, Star, TrendingUp, Target, Home, Building, Building2, Key, Briefcase, Bed, Bath, Maximize, Toilet, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react'
import { motion } from 'framer-motion'
import ChatWidget from '@/components/ChatWidget'
import PropertyFilters, { FILTERS_INITIAL_STATE, PropertyFiltersState, applyFilters } from '@/components/PropertyFilters'
import PropertyPagination from '@/components/PropertyPagination'

interface Property {
  id: string
  seo_title: string
  title: string
  description: string
  price: number
  currency: string
  property_type: string
  purpose: string
  bedrooms?: number
  bathrooms?: number
  half_bathrooms?: number
  area_m2?: number
  zone: string
  city?: string
  amenities?: string[]
  featured_image?: string
}

interface DinamicoTemplateProps {
  agent: {
    name: string
    bio: string
    phone: string
    specialties: string[]
    zones: string[]
    avatar_url?: string
    social_media?: {
      facebook?: string
      instagram?: string
      linkedin?: string
      tiktok?: string
      youtube?: string
    }
  }
}

const DinamicoTemplate: React.FC<DinamicoTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()
  const [properties, setProperties] = useState<Property[]>([])
  const [filters, setFilters] = useState<PropertyFiltersState>(FILTERS_INITIAL_STATE)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(6)

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const subdomain = window.location.hostname.split('.')[0]
        const response = await fetch(`/api/agents/subdomain/${subdomain}/properties`)
        if (response.ok) {
          const data = await response.json()
          setProperties(data)
        }
      } catch (error) {
        console.error('Error fetching properties:', error)
      }
    }
    fetchProperties()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  const formatPrice = (price: number, currency: string) => {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`
  }

  const getSpecialtyIcon = (specialty: string) => {
    const lower = specialty.toLowerCase()
    if (lower.includes('casa')) return Home
    if (lower.includes('depa') || lower.includes('departamento')) return Building
    if (lower.includes('inversion') || lower.includes('inversión')) return TrendingUp
    if (lower.includes('renta')) return Key
    if (lower.includes('oficina')) return Briefcase
    return Building2
  }

  const handlePropertyClick = (seoTitle: string) => {
    navigate(`/property/${seoTitle}`)
  }

  const filteredProperties = applyFilters(properties, filters)
  const propertyTypes = [...new Set(properties.map(p => p.property_type).filter(Boolean))]
  const totalPages = perPage === 0 ? 1 : Math.ceil(filteredProperties.length / perPage)
  const paginatedProperties = perPage === 0
    ? filteredProperties
    : filteredProperties.slice((currentPage - 1) * perPage, currentPage * perPage)
  const showPhone = false
  const showWhatsapp = false

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      {/* Hero */}
      <section className="relative overflow-hidden min-h-screen flex items-center"
        style={{ background: 'linear-gradient(135deg, #1a0000 0%, #3d0000 30%, #7f1d1d 60%, #dc2626 100%)' }}>

        {/* Fondo geométrico dinámico */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            className="absolute -top-32 -right-32 w-96 h-96 border-4 border-yellow-400/20 rounded-full"
          />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
            className="absolute -bottom-20 -left-20 w-64 h-64 border-2 border-orange-400/30 rounded-full"
          />
          <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-ping" />
          <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-orange-400 rounded-full animate-ping delay-700" />
          <div className="absolute top-2/3 right-1/3 w-1 h-1 bg-red-300 rounded-full animate-ping delay-1000" />
        </div>

        <div className="relative container mx-auto px-6 py-20">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">

            {/* Texto */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 bg-yellow-400/20 border border-yellow-400/40 px-4 py-2 rounded-full"
              >
                <Zap size={16} className="text-yellow-400" />
                <span className="text-yellow-300 text-sm font-bold uppercase tracking-wider">Resultados que Impactan</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.7 }}
                className="text-6xl lg:text-7xl font-black leading-none"
              >
                <span className="text-white">{agent.name.split(' ')[0]}</span>
                <br />
                <span className="text-transparent bg-clip-text"
                  style={{ backgroundImage: 'linear-gradient(90deg, #facc15, #f97316)' }}>
                  {agent.name.split(' ').slice(1).join(' ')}
                </span>
              </motion.h1>

              {agent.bio && (
                <motion.p
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-xl text-red-100 leading-relaxed"
                >
                  {agent.bio}
                </motion.p>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="flex flex-col sm:flex-row gap-4"
              >

                {(showPhone || showWhatsapp) && agent.phone && (
                  <>
                    {showPhone && (
                      <a
                        href={`tel:${agent.phone}`}
                        className="group flex items-center justify-center gap-3 px-8 py-4 font-black text-gray-900 rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg shadow-yellow-500/30 text-lg"
                        style={{ background: 'linear-gradient(90deg, #facc15, #f97316)' }}
                      >
                        <Phone size={22} className="group-hover:rotate-12 transition-transform" />
                        {agent.phone}
                      </a>
                    )}
                    {showWhatsapp && (
                      <a 
                        href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] hover:bg-[#1fb855] text-white font-bold rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg"
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp
                      </a>
                    )}
                  </>
                )}
              </motion.div>

              {/* Stats rápidos */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="flex gap-8"
              >
                <div className="text-center">
                  <div className="text-3xl font-black text-yellow-400">{properties.length}</div>
                  <div className="text-red-300 text-xs uppercase tracking-wider">Propiedades</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-orange-400">{agent.zones?.length || 0}</div>
                  <div className="text-red-300 text-xs uppercase tracking-wider">Zonas</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-black text-red-300">{agent.specialties?.length || 0}</div>
                  <div className="text-red-300 text-xs uppercase tracking-wider">Especialidades</div>
                </div>
              </motion.div>

              {/* Social Media */}
              {agent.social_media && Object.values(agent.social_media).some(v => v) && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.1 }}
                  className="flex gap-3"
                >
                  {agent.social_media.facebook && (
                    <a href={agent.social_media.facebook} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-yellow-400/10 hover:bg-yellow-400/30 border border-yellow-400/30 rounded-xl transition-all hover:scale-110">
                      <Facebook className="w-5 h-5 text-yellow-300" />
                    </a>
                  )}
                  {agent.social_media.instagram && (
                    <a href={agent.social_media.instagram} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-yellow-400/10 hover:bg-yellow-400/30 border border-yellow-400/30 rounded-xl transition-all hover:scale-110">
                      <Instagram className="w-5 h-5 text-yellow-300" />
                    </a>
                  )}
                  {agent.social_media.linkedin && (
                    <a href={agent.social_media.linkedin} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-yellow-400/10 hover:bg-yellow-400/30 border border-yellow-400/30 rounded-xl transition-all hover:scale-110">
                      <Linkedin className="w-5 h-5 text-yellow-300" />
                    </a>
                  )}
                  {agent.social_media.tiktok && (
                    <a href={agent.social_media.tiktok} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-yellow-400/10 hover:bg-yellow-400/30 border border-yellow-400/30 rounded-xl transition-all hover:scale-110">
                      <svg className="w-5 h-5 text-yellow-300" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </a>
                  )}
                  {agent.social_media.youtube && (
                    <a href={agent.social_media.youtube} target="_blank" rel="noopener noreferrer"
                      className="p-3 bg-yellow-400/10 hover:bg-yellow-400/30 border border-yellow-400/30 rounded-xl transition-all hover:scale-110">
                      <Youtube className="w-5 h-5 text-yellow-300" />
                    </a>
                  )}
                </motion.div>
              )}
            </motion.div>

            {/* Avatar */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="flex justify-center"
            >
              <div className="relative">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
                  className="absolute -inset-4 rounded-full border-2 border-dashed border-yellow-400/40"
                />
                <div className="absolute -inset-2 rounded-full"
                  style={{ background: 'linear-gradient(45deg, #facc15, #f97316, #dc2626)', opacity: 0.3, filter: 'blur(16px)' }} />
                {agent.avatar_url ? (
                  <img
                    src={agent.avatar_url}
                    alt={agent.name}
                    className="relative w-72 h-72 lg:w-80 lg:h-80 rounded-full object-cover border-4 border-yellow-400/50 shadow-2xl"
                  />
                ) : (
                  <div className="relative w-72 h-72 lg:w-80 lg:h-80 rounded-full flex items-center justify-center border-4 border-yellow-400/50"
                    style={{ background: 'linear-gradient(135deg, #7f1d1d, #dc2626)' }}>
                    <span className="text-8xl font-black text-yellow-400/30">{agent.name.charAt(0)}</span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Propiedades */}
      {properties.length > 0 && (
        <section className="py-20 bg-gray-950">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-4"
            >
              <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/30 px-4 py-2 rounded-full mb-4">
                <Target size={16} className="text-yellow-400" />
                <span className="text-yellow-300 text-sm font-bold uppercase tracking-wider">Portfolio en Acción</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black">
                <span className="text-white">Oportunidades </span>
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #facc15, #f97316)' }}>
                  Reales
                </span>
              </h2>

              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                propertyTypes={propertyTypes}
                totalProperties={properties.length}
                filteredCount={filteredProperties.length}
                colorSchemeKey="red"
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(v => !v)}
              />
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
              {paginatedProperties.map((property, index) => (
                <motion.article
                  key={property.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                  onClick={() => handlePropertyClick(property.seo_title)}
                  className="group cursor-pointer rounded-2xl overflow-hidden border border-red-900/50 hover:border-yellow-400/50 transition-all duration-300 hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #1c0505, #2d0a0a)' }}
                >
                  <div className="relative h-52 overflow-hidden">
                    {property.featured_image ? (
                      <img
                        src={property.featured_image}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"
                        style={{ background: 'linear-gradient(135deg, #3d0000, #7f1d1d)' }}>
                        <Home size={48} className="text-red-700" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute top-3 left-3 bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-black uppercase">
                      {property.purpose}
                    </div>
                    <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-sm text-yellow-400 px-3 py-1 rounded-full text-sm font-black">
                      {formatPrice(property.price, property.currency)}
                    </div>
                  </div>
                  <div className="p-6 space-y-3">
                    <h3 className="font-black text-lg text-white group-hover:text-yellow-400 transition-colors line-clamp-1">
                      {property.title}
                    </h3>
                    <p className="text-red-400 text-sm flex items-center gap-1">
                      <MapPin size={13} />
                      {property.zone}{property.city ? `, ${property.city}` : ''}
                    </p>
                    <div className="flex items-center gap-4 text-gray-500 text-sm pt-2 border-t border-red-900/50">
                      {property.bedrooms && (
                        <span className="flex items-center gap-1 text-gray-400"><Bed size={14} /> {property.bedrooms}</span>
                      )}
                      {property.bathrooms && (
                        <span className="flex items-center gap-1 text-gray-400"><Bath size={14} /> {property.bathrooms}</span>
                      )}
                      {property.half_bathrooms > 0 && (
                        <span className="flex items-center gap-1 text-gray-400"><Toilet size={14} /> {property.half_bathrooms}</span>
                      )}
                      {property.area_m2 && (
                        <span className="flex items-center gap-1 text-gray-400"><Maximize size={14} /> {property.area_m2}m²</span>
                      )}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            <PropertyPagination
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onPerPageChange={setPerPage}
              colorSchemeKey="red"
            />
          </div>
        </section>
      )}

      {/* Especialidades */}
      {agent.specialties?.length > 0 && (
        <section className="py-20" style={{ background: 'linear-gradient(180deg, #0a0000, #1a0505)' }}>
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-black">
                <span className="text-white">Mi </span>
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #facc15, #f97316)' }}>
                  Arsenal
                </span>
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {agent.specialties.map((specialty, index) => {
                const IconComponent = getSpecialtyIcon(specialty)
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    viewport={{ once: true }}
                    className="group text-center p-6 rounded-2xl border border-red-900/50 hover:border-yellow-400/50 transition-all duration-300 hover:scale-105 cursor-default"
                    style={{ background: 'linear-gradient(135deg, #1c0505, #2d0a0a)' }}
                  >
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform"
                      style={{ background: 'linear-gradient(135deg, #facc15, #f97316)' }}>
                      <IconComponent size={28} className="text-gray-900" />
                    </div>
                    <h3 className="font-bold text-white group-hover:text-yellow-400 transition-colors capitalize text-sm">
                      {specialty}
                    </h3>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* Zonas */}
      {agent.zones?.length > 0 && (
        <section className="py-20 bg-gray-950">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-black">
                <span className="text-white">Zona de </span>
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #facc15, #f97316)' }}>
                  Dominio
                </span>
              </h2>
            </motion.div>
            <div className="flex flex-wrap justify-center gap-4">
              {agent.zones.map((zone, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-2 px-6 py-3 rounded-full border border-yellow-400/30 hover:border-yellow-400 transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #1c0505, #2d0a0a)' }}
                >
                  <MapPin size={16} className="text-yellow-400" />
                  <span className="font-bold text-white">{zone}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a0000, #7f1d1d, #dc2626)' }}>
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute inset-0 opacity-20"
          style={{ background: 'radial-gradient(circle at center, #facc15, transparent 60%)' }}
        />
        <div className="relative container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center space-y-8"
          >
            <div className="flex justify-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={28} className="fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white leading-tight">
              ¿Listo para <span className="text-yellow-400">ACTUAR</span>?
            </h2>
            <p className="text-xl text-red-100 max-w-2xl mx-auto">
              No esperes más. {properties.length} oportunidades disponibles hoy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {agent.phone && (
                <>
                  <a href={`tel:${agent.phone}`}
                    className="flex items-center justify-center gap-3 px-10 py-5 font-black text-xl text-gray-900 rounded-2xl transition-all hover:scale-105 shadow-2xl shadow-yellow-500/30"
                    style={{ background: 'linear-gradient(90deg, #facc15, #f97316)' }}>
                    <Phone size={24} /> LLAMAR AHORA
                  </a>
                  <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 px-10 py-5 bg-[#25D366] hover:bg-[#1fb855] text-white font-black text-xl rounded-2xl transition-all hover:scale-105 shadow-2xl">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WHATSAPP
                  </a>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <ChatWidget
        agentName={agent.name}
        agentSubdomain={window.location.hostname.split('.')[0]}
        agentAvatar={agent.avatar_url}
        agentPlan={agent.plan}
      />
    </div>
  )
}

export default DinamicoTemplate
